/**
 * Canvas 2D rocket renderer.
 *
 * Draws a stylized rocket in world coordinates using the provided transform.
 * The rocket is drawn in its local frame (pointing up = +Z) and rotated by
 * the pitch angle (theta) around its center of mass.
 *
 * All drawing is in canvas pixels; world-to-canvas conversion is handled by
 * the transform. This renderer does no physics — it only visualizes state.
 */

import type { Transform } from "./CoordinateTransform";
import type { RocketState } from "../types";

/**
 * Rocket visual parameters (in meters, for scaling with the transform).
 */
export interface RocketVisualParams {
  /** Rocket body length (nose to nozzle), meters. */
  readonly bodyLength: number;
  /** Rocket body diameter, meters. */
  readonly bodyDiameter: number;
  /** Nose cone length, meters. */
  readonly noseLength: number;
  /** Fin height, meters. */
  readonly finHeight: number;
  /** Fin width, meters. */
  readonly finWidth: number;
  /** Exhaust flame max length, meters. */
  readonly flameMaxLength: number;
  /** Rocket body color. */
  readonly bodyColor: string;
  /** Nose cone color. */
  readonly noseColor: string;
  /** Fin color. */
  readonly finColor: string;
  /** Flame color. */
  readonly flameColor: string;
  /** Flame core color. */
  readonly flameCoreColor: string;
}

export const DefaultRocketVisualParams: RocketVisualParams = {
  bodyLength: 6,
  bodyDiameter: 1.2,
  noseLength: 2.5,
  finHeight: 2,
  finWidth: 1,
  flameMaxLength: 4,
  bodyColor: "#3b82f6",
  noseColor: "#94a3b8",
  finColor: "#0f172a",
  flameColor: "#fbbf24",
  flameCoreColor: "#ffffff",
};

/**
 * Draw a rocket on the canvas context.
 *
 * @param ctx - Canvas 2D rendering context.
 * @param transform - World-to-canvas coordinate transform.
 * @param state - Current rocket state (position, pitch, throttle).
 * @param params - Visual parameters.
 * @param targetX - World X of landing target (for drawing target crosshair).
 * @param targetZone - World half-width of acceptable landing zone.
 */
export function drawRocket(
  ctx: CanvasRenderingContext2D,
  transform: Transform,
  state: RocketState,
  throttle: number,
  params: RocketVisualParams = DefaultRocketVisualParams,
): void {
  const { wx, wz, scale } = transform;

  // Save context state.
  ctx.save();

  // Move to rocket center of mass in canvas space.
  const cx = wx(state.x);
  const cy = wz(state.z);

  // Draw the ground reference line and target marker (drawn once by caller,
  // but we draw a small altitude grid here for reference).
  // (Ground and target are drawn by SimulationViewport to avoid redundancy.)

  // Translate to rocket position.
  ctx.translate(cx, cy);

  // Rotate by pitch angle. theta=0 is straight up (+Z), so we rotate by
  // -theta to point the rocket "up" in canvas space (negative Y is up).
  // Positive theta = nose toward +X = clockwise rotation in canvas space.
  ctx.rotate(-state.theta);

  const bl = params.bodyLength;
  const bd = params.bodyDiameter;
  const nl = params.noseLength;
  const fh = params.finHeight;
  const fw = params.finWidth;

  const pxPerMeter = scale;

  // Scale context so we can draw in meters.
  ctx.scale(pxPerMeter, pxPerMeter);

  // ---- Fuselage (body) ----
  ctx.fillStyle = params.bodyColor;
  ctx.beginPath();
  ctx.rect(-bd / 2, -bl / 2, bd, bl);
  ctx.fill();
  ctx.strokeStyle = "rgba(255,255,255,0.15)";
  ctx.lineWidth = 0.05 / pxPerMeter;
  ctx.stroke();

  // ---- Nose cone ----
  ctx.fillStyle = params.noseColor;
  ctx.beginPath();
  ctx.moveTo(0, -bl / 2 - nl);
  ctx.lineTo(bd / 2, -bl / 2);
  ctx.lineTo(-bd / 2, -bl / 2);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // ---- Fins (cross configuration) ----
  ctx.fillStyle = params.finColor;
  const finY = bl / 4; // fins mounted below center
  const finPositions = [
    [fw / 2, 0, fh, fw], // +X fin
    [-fw / 2, 0, fh, fw], // -X fin
    [0, fw / 2, fw, fh], // +Z fin
    [0, -fw / 2, fw, fh], // -Z fin
  ];
  for (const [fx, _fz, fw2, fh2] of finPositions) {
    ctx.beginPath();
    ctx.rect(fx - fw2 / 2, finY - fh2 / 2, fw2, fh2);
    ctx.fill();
    ctx.stroke();
  }

  // ---- Nozzle ----
  ctx.fillStyle = params.finColor;
  ctx.beginPath();
  ctx.rect(-bd / 2 * 0.85, bl / 2, bd * 0.85, 0.9);
  ctx.fill();
  ctx.stroke();

  // ---- Exhaust flame ----
  if (throttle > 0.05) {
    const flameLen = params.flameMaxLength * throttle;
    const flameWidth = bd * 0.9 * (0.7 + 0.3 * throttle);

    // Outer flame.
    ctx.fillStyle = params.flameColor;
    ctx.beginPath();
    ctx.moveTo(-flameWidth / 2, bl / 2 + 0.9);
    ctx.lineTo(0, bl / 2 + 0.9 + flameLen);
    ctx.lineTo(flameWidth / 2, bl / 2 + 0.9);
    ctx.closePath();
    ctx.fill();

    // Inner core.
    ctx.fillStyle = params.flameCoreColor;
    ctx.beginPath();
    ctx.moveTo(-flameWidth / 4, bl / 2 + 0.9);
    ctx.lineTo(0, bl / 2 + 0.9 + flameLen * 0.8);
    ctx.lineTo(flameWidth / 4, bl / 2 + 0.9);
    ctx.closePath();
    ctx.fill();
  }

  // Restore context (undoes scale, rotate, translate).
  ctx.restore();
}

/**
 * Draw the landing target crosshair and acceptable zone on the canvas.
 *
 * @param ctx - Canvas 2D rendering context.
 * @param transform - World-to-canvas coordinate transform.
 * @param targetX - World X of landing target.
 * @param zoneHalfWidth - Half-width of acceptable landing zone (meters).
 */
export function drawLandingTarget(
  ctx: CanvasRenderingContext2D,
  transform: Transform,
  targetX: number,
  zoneHalfWidth: number,
): void {
  const { wx, scale } = transform;
  const groundY = transform.wz(0);

  ctx.save();
  ctx.translate(wx(targetX), groundY);

  // Target crosshair.
  ctx.strokeStyle = "#ef4444";
  ctx.lineWidth = 1.5 / scale;
  ctx.setLineDash([6, 4]);
  ctx.beginPath();
  ctx.moveTo(-zoneHalfWidth, 0);
  ctx.lineTo(zoneHalfWidth, 0);
  ctx.moveTo(0, -10 / scale);
  ctx.lineTo(0, 10 / scale);
  ctx.stroke();
  ctx.setLineDash([]);

  // Center marker.
  ctx.fillStyle = "#ef4444";
  ctx.beginPath();
  ctx.arc(0, 0, 2 / scale, 0, 2 * Math.PI);
  ctx.fill();

  // Zone outline.
  ctx.strokeStyle = "rgba(239, 68, 68, 0.35)";
  ctx.lineWidth = 1 / scale;
  ctx.beginPath();
  ctx.moveTo(-zoneHalfWidth, -2 / scale);
  ctx.lineTo(-zoneHalfWidth, 2 / scale);
  ctx.moveTo(zoneHalfWidth, -2 / scale);
  ctx.lineTo(zoneHalfWidth, 2 / scale);
  ctx.stroke();

  ctx.restore();
}

/**
 * Draw ground line with altitude reference ticks.
 *
 * @param ctx - Canvas 2D rendering context.
 * @param transform - World-to-canvas coordinate transform.
 * @param worldX - Half-width of visible world (meters).
 */
export function drawGround(
  ctx: CanvasRenderingContext2D,
  transform: Transform,
  worldX: number,
): void {
  const { wx, wz, scale } = transform;
  const groundY = wz(0);

  ctx.save();

  // Ground line.
  ctx.strokeStyle = "#334155";
  ctx.lineWidth = 2 / scale;
  ctx.beginPath();
  ctx.moveTo(wx(-worldX), groundY);
  ctx.lineTo(wx(worldX), groundY);
  ctx.stroke();

  // Altitude ticks every 100m.
  ctx.strokeStyle = "#5b6674";
  ctx.lineWidth = 1 / scale;
  ctx.font = `10px ${"ui-monospace, SFMono-Regular, Menlo, Consolas, monospace"}`;
  ctx.fillStyle = "#5b6674";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";

  const tickInterval = 100;
  for (let h = tickInterval; h <= transform.wz(0) / scale && h < 10000; h += tickInterval) {
    // We can't easily invert for altitude ticks; instead tick X axis.
  }

  // X-axis ticks every 50m.
  const xTick = 50;
  const xMin = -worldX;
  const xMax = worldX;
  for (let x = Math.ceil(xMin / xTick) * xTick; x <= xMax; x += xTick) {
    const px = wx(x);
    ctx.beginPath();
    ctx.moveTo(px, groundY - 6 / scale);
    ctx.lineTo(px, groundY + 6 / scale);
    ctx.stroke();
    ctx.fillText(`${x} m`, px, groundY + 10 / scale);
  }

  ctx.restore();
}
